import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Typography, Stack, Button, IconButton, Tooltip, TextField, InputAdornment, Grid,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider, Alert, Snackbar, CircularProgress, Chip, MenuItem,
} from '@mui/material';
import FolderSharedRoundedIcon from '@mui/icons-material/FolderSharedRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import UploadFileRoundedIcon from '@mui/icons-material/UploadFileRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_BORDER } from '../theme';
import {
    card, solidBtn, ghostBtn, tonalBtn, field, Panel, EmptyState, ConfirmDialog, SkeletonCards, PageHeader, FileBadge, fmtSize,
} from '../components/uiKit';
import http, { apiErrorMessage } from '../Api/http';
import { PostMyDocument, UpdateMyDocument, GetMyDocuments, GetMyDocumentById, DeleteMyDocument, GetDocumentTypes } from '../Api/Api';
import { ONBOARDING_DOCS, HIDDEN_DOC_KEYS, CATEGORIES } from '../components/DocumentsComps/DocumentRequestsTab';

const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_EXT = ['pdf', 'jpg', 'jpeg', 'png', 'doc', 'docx'];
const ACCEPT = ALLOWED_EXT.map((e) => `.${e}`).join(',');

const label = { fontSize: 12.5, fontWeight: 600, color: '#334155', mb: 0.6 };
const EMPTY_FORM = { documentName: '', category: '', description: '' };

const fmtDate = (v) => {
    if (!v) return '—';
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

// Returns an error string, or '' when the file is acceptable.
const checkFile = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) return 'Only PDF, JPG, PNG, DOC or DOCX files are allowed.';
    if (file.size > MAX_BYTES) return 'The file is larger than 10 MB.';
    return '';
};

export default function MyDocumentsPage() {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    // Names already on file (from the unfiltered list), so an uploaded type leaves the dropdown.
    const [uploadedNames, setUploadedNames] = useState(null);
    // Same list the Documents -> Requests screen offers; the built-in list stands in
    // until the server's arrives (or if the call fails).
    const [knownTypes, setKnownTypes] = useState(() => ONBOARDING_DOCS.map((d) => ({ key: d.key, label: d.name })));
    useEffect(() => {
        let off = false;
        http.get(GetDocumentTypes).then(({ data: body }) => {
            const d = body?.data;
            const list = Array.isArray(d) ? d : (d?.types || d?.documentTypes || d?.items || []);
            if (!off && !body?.error && list.length) setKnownTypes(list.filter((t) => t?.key && !HIDDEN_DOC_KEYS.includes(t.key)).map((t) => ({ key: t.key, label: t.label || t.key })));
        }).catch(() => { /* keep the built-in list */ });
        return () => { off = true; };
    }, []);
    const docOptions = useMemo(() => knownTypes.map((k) => ({
        id: k.key, name: k.label,
        category: ONBOARDING_DOCS.find((o) => o.key === k.key)?.category || 'General',
    })), [knownTypes]);
    const norm = (n) => String(n || '').trim().toLowerCase();
    // Standard types not yet uploaded; while editing, the document's own name stays selectable.
    const optionsFor = (ownName) => {
        const own = norm(ownName);
        const opts = docOptions.filter((o) => norm(o.name) === own || !(uploadedNames && uploadedNames.has(norm(o.name))));
        return own && !opts.some((o) => norm(o.name) === own) ? [{ id: `own-${own}`, name: ownName, category: '' }, ...opts] : opts;
    };
    const addOptions = useMemo(() => optionsFor(''), [docOptions, uploadedNames]);
    const allUploaded = uploadedNames != null && docOptions.length > 0 && addOptions.length === 0;
    const [file, setFile] = useState(null);
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const fileInput = useRef(null);
    const [loadingOne, setLoadingOne] = useState(false);
    const editFetch = useRef(0);

    const [confirmDel, setConfirmDel] = useState(null);
    const [snack, setSnack] = useState({ msg: '', severity: 'success' });
    const notify = (msg, severity = 'success') => setSnack({ msg, severity });

    // Debounce the search box so every keystroke isn't a request.
    useEffect(() => {
        const t = setTimeout(() => setQuery(search.trim()), 350);
        return () => clearTimeout(t);
    }, [search]);

    const load = useCallback(async () => {
        setLoading(true);
        setLoadError('');
        try {
            const { data: body } = await http.get(GetMyDocuments, { params: query ? { search: query } : undefined });
            if (body?.error) throw new Error(body.message || 'Could not load your documents.');
            const items = body?.data?.items || [];
            setDocs(items);
            if (!query) setUploadedNames(new Set(items.map((d) => norm(d.documentName))));
        } catch (err) {
            // The API answers "no documents" with a 404 + error:true — that is an
            // empty list, not a failure.
            if (err?.response?.status === 404) { setDocs([]); if (!query) setUploadedNames(new Set()); }
            else setLoadError(err?.response || err?.request ? apiErrorMessage(err, 'Could not load your documents.') : err.message);
        } finally {
            setLoading(false);
        }
    }, [query]);

    useEffect(() => { load(); }, [load]);

    const openAdd = () => {
        setEditing(null); setForm(EMPTY_FORM); setFile(null); setFormError(''); setDialogOpen(true);
    };
    const openEdit = async (d) => {
        setEditing(d);
        setForm({ documentName: d.documentName || '', category: d.category || '', description: d.description || '' });
        setFile(null); setFormError(''); setDialogOpen(true);
        // Fetch the document fresh; the list row stays as the fallback.
        const token = ++editFetch.current;
        setLoadingOne(true);
        try {
            const { data: body } = await http.get(`${GetMyDocumentById}/${d.id}`);
            if (body?.error || !body?.data || token !== editFetch.current) return;
            const fresh = body.data;
            setEditing((cur) => (cur && cur.id === d.id ? { ...cur, ...fresh } : cur));
            setForm({ documentName: fresh.documentName || '', category: fresh.category || '', description: fresh.description || '' });
        } catch {
            // keep the list row
        } finally {
            if (token === editFetch.current) setLoadingOne(false);
        }
    };
    const closeDialog = () => { if (!saving) { editFetch.current += 1; setLoadingOne(false); setDialogOpen(false); } };

    const pickFile = (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        const problem = checkFile(f);
        if (problem) { setFormError(problem); return; }
        setFormError('');
        setFile(f);
    };

    const save = async () => {
        const documentName = form.documentName.trim();
        if (!documentName) { setFormError('Enter a document name.'); return; }
        if (!editing && !file) { setFormError('Choose a file to upload.'); return; }

        const fd = new FormData();
        fd.append('documentName', documentName);
        fd.append('category', form.category.trim());
        fd.append('description', form.description.trim());
        if (file) fd.append('file', file);

        setSaving(true);
        setFormError('');
        try {
            const { data: body } = editing
                ? await http.put(`${UpdateMyDocument}/${editing.id}`, fd)
                : await http.post(PostMyDocument, fd);
            if (body?.error) throw new Error(body.message || 'Could not save the document.');

            setDialogOpen(false);
            notify(editing ? 'Document updated.' : 'Document uploaded.');
            await load();
        } catch (err) {
            setFormError(err?.response || err?.request ? apiErrorMessage(err, 'Could not save the document.') : err.message);
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        const d = confirmDel;
        setConfirmDel(null);
        try {
            const { data: body } = await http.delete(`${DeleteMyDocument}/${d.id}`);
            if (body?.error) throw new Error(body.message || 'Could not delete the document.');
            notify(`"${d.documentName}" deleted.`);
            await load();
        } catch (err) {
            notify(err?.response || err?.request ? apiErrorMessage(err, 'Could not delete the document.') : err.message, 'error');
        }
    };

    const view = (d) => window.open(d.url, '_blank', 'noopener,noreferrer');

    // Fetching the blob lets the browser save it under its own name; if the
    // storage host blocks cross-origin reads, fall back to a plain link.
    const download = async (d) => {
        const save = (href) => {
            const a = document.createElement('a');
            a.href = href;
            a.download = d.fileName || d.documentName;
            a.rel = 'noopener';
            document.body.appendChild(a);
            a.click();
            a.remove();
        };
        try {
            const res = await fetch(d.url);
            if (!res.ok) throw new Error('fetch failed');
            const blobUrl = URL.createObjectURL(await res.blob());
            save(blobUrl);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 4000);
        } catch {
            save(d.url);
        }
    };

    const iconBtn = { color: '#94A3B8', '&:hover': { color: PRIMARY, bgcolor: PRIMARY_LIGHT } };
    const hasQuery = Boolean(query);
    const chipLabel = useMemo(() => `${docs.length} document${docs.length === 1 ? '' : 's'}`, [docs.length]);

    return (
        <Box sx={{ p: 2 }}>
            <PageHeader title="My Documents" subtitle="Your personal file store — only you can see these" icon={FolderSharedRoundedIcon}>
                <Tooltip arrow title="Reload">
                    <span>
                        <IconButton onClick={load} disabled={loading} sx={{ width: 40, height: 40, ...ghostBtn }}>
                            <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                        </IconButton>
                    </span>
                </Tooltip>
                {allUploaded
                    ? <Typography sx={{ fontSize: 12.5, color: '#64748B', alignSelf: 'center' }}>You have uploaded all the standard documents.</Typography>
                    : <Button startIcon={<AddRoundedIcon />} onClick={openAdd} sx={{ ...solidBtn, height: 40, px: 2.2 }}>Add document</Button>}
            </PageHeader>

            <Panel
                title="Documents" icon={FolderSharedRoundedIcon}
                chip={!loading && !loadError ? chipLabel : undefined}
                action={(
                    <TextField
                        size="small" placeholder="Search name, category or description" value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        sx={{ ...field, width: { xs: '100%', sm: 300 } }}
                        slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: '#94A3B8' }} /></InputAdornment> } }}
                    />
                )}
            >
                {loading && <SkeletonCards count={6} height={150} />}

                {!loading && loadError && (
                    <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
                        <ErrorOutlineRoundedIcon sx={{ fontSize: 38, color: '#F87171' }} />
                        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0F172A', mt: 1 }}>Couldn’t load your documents</Typography>
                        <Typography sx={{ fontSize: 13, color: '#6B7280', mt: 0.5 }}>{loadError}</Typography>
                        <Button onClick={load} startIcon={<RefreshRoundedIcon />} sx={{ ...tonalBtn, mt: 2, height: 40, px: 2.4 }}>Try again</Button>
                    </Box>
                )}

                {!loading && !loadError && docs.length === 0 && (
                    <EmptyState
                        icon={FolderSharedRoundedIcon}
                        title={hasQuery ? 'No documents match your search' : 'No documents yet'}
                        hint={hasQuery ? 'Try a different name or category.' : 'Add your ID proofs, certificates or anything else you want to keep handy.'}
                    />
                )}

                {!loading && !loadError && docs.length > 0 && (
                    <Box sx={{ p: 1.8 }}>
                        <Grid container spacing={1.5}>
                            {docs.map((d) => (
                                <Grid size={{ xs: 12, md: 6, xl: 4 }} key={d.id}>
                                    <Box sx={{ ...card, p: 1.8, height: '100%', display: 'flex', flexDirection: 'column', gap: 1.2, borderRadius: '9px' }}>
                                        <FileBadge fileName={d.fileName} fileSize={d.sizeBytes} title={d.documentName} />
                                        <Stack direction="row" sx={{ alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                                            {d.category && (
                                                <Chip label={d.category} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY, border: `1px solid ${PRIMARY_BORDER}` }} />
                                            )}
                                            <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>
                                                {fmtSize(d.sizeBytes)} · Added {fmtDate(d.createdOn)}
                                            </Typography>
                                        </Stack>
                                        <Typography sx={{ fontSize: 12.5, color: '#64748B', lineHeight: 1.5, flexGrow: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            {d.description || 'No description.'}
                                        </Typography>
                                        <Divider />
                                        <Stack direction="row" sx={{ justifyContent: 'flex-end', gap: 0.3 }}>
                                            <Tooltip arrow title="View"><IconButton size="small" onClick={() => view(d)} sx={iconBtn}><VisibilityRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                            <Tooltip arrow title="Download"><IconButton size="small" onClick={() => download(d)} sx={iconBtn}><DownloadRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                            <Tooltip arrow title="Edit"><IconButton size="small" onClick={() => openEdit(d)} sx={iconBtn}><EditRoundedIcon sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                                            <Tooltip arrow title="Delete">
                                                <IconButton size="small" onClick={() => setConfirmDel(d)} sx={{ color: '#94A3B8', '&:hover': { color: '#DC2626', bgcolor: '#FEF2F2' } }}>
                                                    <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Panel>

            <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <DialogTitle sx={{ p: 2.5, pb: 1.5 }}>
                    <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box>
                            <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>{editing ? 'Edit document' : 'Add document'}</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#98A0AE' }}>PDF, JPG, PNG, DOC or DOCX, up to 10 MB</Typography>
                        </Box>
                        <IconButton onClick={closeDialog} size="small" disabled={saving}><CloseRoundedIcon /></IconButton>
                    </Stack>
                </DialogTitle>
                <Divider />
                <DialogContent sx={{ p: 2.5 }}>
                    {formError && <Alert severity="error" onClose={() => setFormError('')} sx={{ mb: 2, borderRadius: '7px', fontSize: 12.5 }}>{formError}</Alert>}

                    <Typography sx={label}>Document name *</Typography>
                    <TextField
                        select fullWidth size="small" disabled={saving || loadingOne}
                        value={optionsFor(form.documentName).find((o) => norm(o.name) === norm(form.documentName))?.id || ''}
                        onChange={(e) => {
                            const o = optionsFor(editing?.documentName).find((x) => x.id === e.target.value);
                            if (o) setForm((f) => ({ ...f, documentName: o.name, category: o.category || f.category }));
                        }}
                        slotProps={{ select: { displayEmpty: true, MenuProps: { slotProps: { paper: { sx: { maxHeight: 320 } } } } } }}
                        sx={{ ...field, mb: 2 }}
                    >
                        <MenuItem value="" disabled sx={{ fontSize: 13.5, color: '#94A3B8' }}>Select a document</MenuItem>
                        {optionsFor(editing?.documentName).map((o) => (
                            <MenuItem key={o.id} value={o.id} sx={{ display: 'block', py: 0.8 }}>
                                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{o.name}</Typography>
                                {o.category && <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{o.category}</Typography>}
                            </MenuItem>
                        ))}
                    </TextField>

                    <Typography sx={label}>Category</Typography>
                    <TextField
                        select fullWidth size="small" disabled={saving || loadingOne}
                        value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                        slotProps={{ select: { displayEmpty: true } }}
                        sx={{ ...field, mb: 2 }}
                    >
                        <MenuItem value="" sx={{ fontSize: 13.5, color: '#94A3B8' }}>None</MenuItem>
                        {[...CATEGORIES, ...(form.category && !CATEGORIES.includes(form.category) ? [form.category] : [])].map((c) => (
                            <MenuItem key={c} value={c} sx={{ fontSize: 13.5 }}>{c}</MenuItem>
                        ))}
                    </TextField>

                    <Typography sx={label}>Description</Typography>
                    <TextField
                        fullWidth size="small" multiline minRows={2} placeholder="Optional" disabled={saving || loadingOne}
                        value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                        sx={{ ...field, mb: 2 }}
                    />

                    <Typography sx={label}>{editing ? 'Replace file (optional)' : 'File *'}</Typography>
                    <input ref={fileInput} type="file" hidden accept={ACCEPT} onChange={pickFile} />
                    <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, p: 1.2, border: '1.5px dashed #D8DEE8', borderRadius: '9px', bgcolor: '#F8FAFC' }}>
                        <Button startIcon={<UploadFileRoundedIcon />} onClick={() => fileInput.current?.click()} disabled={saving} sx={{ ...tonalBtn, height: 36, flexShrink: 0 }}>
                            Choose file
                        </Button>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: '#0F172A' }} noWrap>
                                {file ? file.name : (editing?.fileName || 'No file chosen')}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>
                                {file ? fmtSize(file.size) : (editing ? 'Keeping the current file' : '')}
                            </Typography>
                        </Box>
                    </Stack>
                </DialogContent>
                <Divider />
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={closeDialog} disabled={saving} sx={{ ...ghostBtn, height: 40, px: 2 }}>Cancel</Button>
                    <Button
                        onClick={save} disabled={saving || loadingOne}
                        startIcon={(saving || loadingOne) ?<CircularProgress size={16} thickness={5} sx={{ color: PRIMARY }} /> : undefined}
                        sx={{ ...solidBtn, px: 2.5, height: 40 }}
                    >
                        {saving ? 'Saving…' : (editing ? 'Save changes' : 'Upload')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ConfirmDialog
                open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)} onConfirm={remove}
                title="Delete this document?"
                body={<><strong style={{ color: '#111827' }}>{confirmDel?.documentName}</strong> and its file will be permanently removed. This cannot be undone.</>}
                confirmLabel="Delete"
            />

            <Snackbar open={Boolean(snack.msg)} autoHideDuration={3200} onClose={() => setSnack({ msg: '', severity: 'success' })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack({ msg: '', severity: 'success' })} severity={snack.severity} variant="filled" sx={{ borderRadius: '7px' }}>{snack.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
